import React, { lazy } from "react";
import { DateFieldDefaults } from "@datanomik/core-ui";
import { CashPoolingIcon } from "./shared/CashPoolingIcon";
import { CashPoolingAccessGuard } from "./components/CashPoolingAccessGuard";

// Lazy load components for each entrypoint
const OwnerListPage = lazy(() =>
  import("./owner-list/OwnerListPage").then((m) => ({ default: m.OwnerListPage })),
);
const RuleListPage = lazy(() =>
  import("./rule-list/RuleListPage").then((m) => ({ default: m.RuleListPage })),
);

const CashPoolingCredentialsPage = lazy(() =>
  import("./cash-pooling-credentials/CashPoolingCredentialsPage").then((m) => ({ default: m.CashPoolingCredentialsPage })),
);

const CashPoolingSettingsPage = lazy(() =>
  import("./cash-pooling-settings/CashPoolingSettingsPage").then((m) => ({ default: m.CashPoolingSettingsPage })),
);

const RolesRouteRedirect = lazy(() =>
  import("./cash-pooling-settings/page/RolesRouteRedirect").then((m) => ({ default: m.RolesRouteRedirect })),
);

const TransferHistoryPage = lazy(() =>
  import("./transfer-history/TransferHistoryPage").then((m) => ({ default: m.TransferHistoryPage })),
);

const RuleExecutionsPage = lazy(() =>
  import("./rule-executions/RuleExecutionsPage").then((m) => ({ default: m.RuleExecutionsPage })),
);

const RuleExecutionDetailPage = lazy(() =>
  import("./rule-executions/RuleExecutionDetailPage").then((m) => ({ default: m.RuleExecutionDetailPage })),
);

/**
 * Cash Pooling feature routes configuration
 *
 * Route structure:
 * - /cash-pooling/ -> list of owners (supports ?search=query)
 * - /cash-pooling/:ownerId -> list of rules for a specific owner
 *
 * Rule creation and editing both happen in-place via drawers (CreateRuleDrawer/EditRuleDrawer),
 * not as dedicated routes.
 */

// Every route element is wrapped in CashPoolingAccessGuard, which gates the module on the caller's /me role.
export const getCashPoolingRoutes = () => [
  {
    // List of owners - /cash-pooling/
    path: "",
    key: "cash-pooling", // Use parent key for permissions
    label: "Cash Pooling",
    description: "The intelligent way to transfer resources.",
    element: <OwnerListPage />,
    icon: <CashPoolingIcon />,
  },
  {
    path: "transfer-history",
    label: "Transfer history",
    key: "cash-pooling-transfer-history",
    element: <TransferHistoryPage />,
    hiddenPageTitle: true,
  },
  {
    path: "rule-executions",
    label: "Rule executions",
    key: "cash-pooling-rule-executions",
    element: <RuleExecutionsPage />,
    hiddenPageTitle: true,
  },
  {
    path: "rule-executions/:executionId",
    label: "Rule execution",
    key: "cash-pooling-rule-execution-detail",
    element: <RuleExecutionDetailPage />,
    hiddenPageTitle: true,
  },
  {
    // List of rules for a specific owner - /cash-pooling/:ownerId
    path: ":ownerId",
    label: "OWNER_NAME", // Dynamic label based on owner
    key: "cash-pooling-rule-list",
    element: <RuleListPage />,
    hiddenPageTitle: true,
  },
  {
    path: "credentials",
    label: "Credentials",
    key: "cash-pooling-credentials",
    element: <CashPoolingCredentialsPage />,
    hiddenPageTitle: true,
  },
  {
    path: "settings",
    label: "Settings",
    key: "cash-pooling-settings",
    element: <CashPoolingSettingsPage />,
    hiddenPageTitle: true,
  },
  {
    // Legacy path: the access-levels screen now lives inside /settings (Security tab).
    path: "roles",
    label: "Roles",
    key: "cash-pooling-roles",
    element: <RolesRouteRedirect />,
    hiddenPageTitle: true,
  },
].map((route) => ({
  ...route,
  element: (
    <DateFieldDefaults min={null}>
      <CashPoolingAccessGuard>{route.element}</CashPoolingAccessGuard>
    </DateFieldDefaults>
  ),
}));
